import axios from "axios";
import {
  Camera,
  Copy,
  ImageIcon,
  Link,
  Mic,
  Plus,
  Send,
  Sparkles,
  Square,
  X,
  HeadphonesIcon,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Ticket,
  ArrowUpCircle,
} from "lucide-react";
import React, { useRef, useState, useEffect } from "react";
import googleIcon from "../../img/googleIcon.svg";
import wikipediaIcon from "../../img/wikipidiaIcon.svg";
import amazonIcon from "../../img/amazonIcon.svg";
import alibabaIcon from "../../img/alibabaIcon.svg";
import { useNavigate } from "react-router-dom";

// ─── Mode Toggle Component ────────────────────────────────────────────────────
const ModeToggle = ({ mode, onChange }) => (
  <div className="flex items-center gap-1 bg-white/15 rounded-xl p-1">
    <button
      onClick={() => onChange("general")}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all duration-200 ${
        mode === "general"
          ? "bg-white text-blue-700 shadow-sm"
          : "text-white/80 hover:text-white hover:bg-white/10"
      }`}
    >
      <MessageSquare className="w-3 h-3" />
      General
    </button>
    <button
      onClick={() => onChange("support")}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all duration-200 ${
        mode === "support"
          ? "bg-white text-indigo-700 shadow-sm"
          : "text-white/80 hover:text-white hover:bg-white/10"
      }`}
    >
      <HeadphonesIcon className="w-3 h-3" />
      Support
    </button>
  </div>
);

// ─── Extract Wistia embed URL ─────────────────────────────────────────────────
const getWistiaEmbedUrl = (text) => {
  if (!text) return null;
  const match = text.match(
    /https?:\/\/[^\s]+\.wistia\.com\/medias\/([a-z0-9]+)/i,
  );
  if (!match) return null;
  return `https://fast.wistia.net/embed/iframe/${match[1]}`;
};

// Strip all URLs and trailing whitespace/labels like "OR" from the text
const cleanResponseText = (text) => {
  if (!text) return "";
  return text
    .replace(/https?:\/\/\S+/g, "") // remove URLs
    .replace(/\n\s*OR\s*\n/gi, "\n") // remove standalone OR lines
    .replace(/\n{3,}/g, "\n\n") // collapse excess blank lines
    .trim();
};

// ─── Support Message Bubble ───────────────────────────────────────────────────
const SupportMessage = ({ msg, onEscalate }) => {
  // Check both chatResponseUser and chatResponseTechnical for a video URL
  const embedUrl =
    getWistiaEmbedUrl(msg.chatResponseTechnical) ||
    getWistiaEmbedUrl(msg.chatResponseUser);

  const cleanText = cleanResponseText(msg.chatResponseUser);

  return (
    <div className="space-y-1.5">
      {/* Main user-facing response — URLs stripped */}
      <p className="whitespace-pre-wrap text-sm leading-relaxed">{cleanText}</p>

      {/* Embedded training video */}
      {embedUrl && (
        <div className="mt-2 rounded-xl overflow-hidden border border-slate-100 shadow-sm bg-black">
          <div className="px-2 pt-2 pb-1 bg-slate-800 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="w-2 h-2 rounded-full bg-yellow-400" />
            <span className="w-2 h-2 rounded-full bg-green-400" />
            <span className="text-[10px] text-slate-400 ml-1 font-medium">
              Training Video
            </span>
          </div>
          <iframe
            src={embedUrl}
            title="Training Video"
            allowFullScreen
            className="w-full"
            style={{ height: "160px", border: "none" }}
          />
        </div>
      )}

      {/* Status badges */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {msg.resolvedByAI && (
          <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-100 px-2 py-0.5 rounded-full font-bold">
            <CheckCircle2 className="w-3 h-3" />
            Resolved by AI
          </span>
        )}
        {msg.escalate && (
          <span className="inline-flex items-center gap-1 text-[10px] bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full font-bold">
            <AlertCircle className="w-3 h-3" />
            Escalated
          </span>
        )}
        {msg.ticketNo && (
          <span className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-full font-bold">
            <Ticket className="w-3 h-3" />
            Ticket #{msg.ticketNo}
          </span>
        )}
      </div>

      {/* Escalate link — shown on every response that hasn't been escalated yet */}
      {!msg.escalate && (
        <button
          onClick={onEscalate}
          className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-violet-600 transition-colors mt-1"
        >
          <ArrowUpCircle className="w-3 h-3" />
          Escalate to support team
        </button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ChatAssistantModal = ({ setIsAssistantOpen }) => {
  const API_PATHS = JSON.parse(localStorage.getItem("apiPaths") || "[]");
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const COMPANY_CODE = localStorage.getItem("companyCode") || "SUNRISEWEB";
  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const USER_ID = localStorage.getItem("userId") || "admin";

  // ── Mode state ──────────────────────────────────────────────────────────────
  const [mode, setMode] = useState("general"); // "general" | "support"
  const [supportChatHistory, setSupportChatHistory] = useState([]);

  // ── Recording state ─────────────────────────────────────────────────────────
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [audioMessages, setAudioMessages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [targetCategory, setTargetCategory] = useState("");

  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const assistantRef = useRef(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  // ── WAV helpers ─────────────────────────────────────────────────────────────
  function bufferToWave(abuffer, len) {
    let numOfChan = abuffer.numberOfChannels,
      length = len * numOfChan * 2 + 44,
      buffer = new ArrayBuffer(length),
      view = new DataView(buffer),
      channels = [],
      i,
      sample,
      offset = 0,
      pos = 0;

    setUint32(0x46464952);
    setUint32(length - 8);
    setUint32(0x45564157);
    setUint32(0x20746d66);
    setUint32(16);
    setUint16(1);
    setUint16(numOfChan);
    setUint32(abuffer.sampleRate);
    setUint32(abuffer.sampleRate * 2 * numOfChan);
    setUint16(numOfChan * 2);
    setUint16(16);
    setUint32(0x61746164);
    setUint32(length - pos - 4);

    for (i = 0; i < abuffer.numberOfChannels; i++)
      channels.push(abuffer.getChannelData(i));

    while (pos < length) {
      for (i = 0; i < numOfChan; i++) {
        sample = Math.max(-1, Math.min(1, channels[i][offset]));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(pos, sample, true);
        pos += 2;
      }
      offset++;
    }

    return new Blob([buffer], { type: "audio/wav" });

    function setUint16(data) {
      view.setUint16(pos, data, true);
      pos += 2;
    }
    function setUint32(data) {
      view.setUint32(pos, data, true);
      pos += 4;
    }
  }

  async function convertToMonoWav(audioBuffer, targetSampleRate = 16000) {
    const rawData = audioBuffer.getChannelData(0);
    const ratio = audioBuffer.sampleRate / targetSampleRate;
    const newLength = Math.max(1, Math.floor(rawData.length / ratio));
    const resampledData = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
      resampledData[i] = rawData[Math.floor(i * ratio)];
    }
    const mockBuffer = {
      numberOfChannels: 1,
      sampleRate: targetSampleRate,
      getChannelData: () => resampledData,
      length: newLength,
    };
    return bufferToWave(mockBuffer, newLength);
  }

  // ── Recording ───────────────────────────────────────────────────────────────
  const toggleRecording = async () => {
    if (!isRecording) {
      try {
        setTranscription("");
        setInputValue("");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        streamRef.current = stream;
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = async () => {
          setIsProcessing(true);
          setIsRecording(false);

          const recordedBlob = new Blob(chunksRef.current);
          const audioUrl = URL.createObjectURL(recordedBlob);
          const arrayBuffer = await recordedBlob.arrayBuffer();
          const audioContext = new AudioContext();
          const decodedBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const wav16kBlob = await convertToMonoWav(decodedBuffer, 16000);

          setAudioMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              blob: recordedBlob,
              url: audioUrl,
              createdAt: new Date().toLocaleTimeString(),
            },
          ]);

          const formData = new FormData();
          formData.append("audioFile", wav16kBlob, "recorded.wav");

          try {
            const response = await axios.post(
              `${BASE_URL}/api/CRMAIAssistantRequestAIAVoice/transcribe?userId=admin`,
              formData,
              { headers: { "Content-Type": "multipart/form-data" } },
            );
            const text = response.data.transcription || "";
            setTranscription(text);
            if (imagePreview) setTargetCategory(text);
            else setInputValue(text);
            setTimeout(
              () => document.querySelector("#cubina-input")?.focus(),
              50,
            );
          } catch (err) {
            console.error("Backend upload failed:", err);
            setInputValue("");
          } finally {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            setIsProcessing(false);
          }
        };

        mediaRecorder.start();
        setIsRecording(true);
      } catch (err) {
        console.error("Microphone error:", err);
        alert("Microphone access denied or unavailable");
        setIsRecording(false);
      }
    } else {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
    }
  };

  // ── Platform links ──────────────────────────────────────────────────────────
  const PlatformLinks = ({ links }) => {
    if (!links) return null;
    const platforms = [
      {
        id: "google",
        label: "Google",
        icon: <img src={googleIcon} alt="Google" className="w-5 h-5" />,
      },
      {
        id: "amazon",
        label: "Amazon",
        icon: <img src={amazonIcon} alt="Amazon" className="w-5 h-5" />,
      },
      {
        id: "alibaba",
        label: "Alibaba",
        icon: <img src={alibabaIcon} alt="Alibaba" className="w-5 h-5" />,
      },
      {
        id: "wikipedia",
        label: "Wiki",
        icon: <img src={wikipediaIcon} alt="Wikipedia" className="w-5 h-5" />,
      },
    ];
    return (
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
        {platforms.map(
          (p) =>
            links[p.id] && (
              <a
                key={p.id}
                href={links[p.id]}
                target="_blank"
                rel="noopener noreferrer"
                title={p.label}
                className="opacity-90 hover:grayscale-0 hover:opacity-100 transition-all duration-200 transform hover:scale-110"
              >
                {p.icon}
              </a>
            ),
        )}
      </div>
    );
  };

  // ── Image handling ──────────────────────────────────────────────────────────
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleImageSubmit = async () => {
    if (!selectedImage) {
      alert("Please select an image first");
      return;
    }
    const preview = imagePreview;
    const imageFile = selectedImage;
    const category = targetCategory.trim() || "General";

    setSelectedImage(null);
    setImagePreview(null);
    setTargetCategory("");

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: `Identify image in: ${category}`,
        image: preview,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
    setLoading(true);

    const formData = new FormData();
    formData.append("Image", imageFile);
    formData.append("TargetCategory", category);
    formData.append("Prompt", "Identify object and provide details");

    try {
      const response = await axios.post(
        `${BASE_URL}/api/ai/object-recognition/identify`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const res = response.data;
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          isRichResponse: true,
          objectName: res.object_name,
          confidence: res.confidence,
          category: res.target_category || category,
          description: res.description,
          links: res.reference_links,
          OemPartNumber: res.oem_part_number,
          alternativePartNumbers: res.aftermarket_part_numbers,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Image identification failed.",
          time: "Now",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ── Camera ──────────────────────────────────────────────────────────────────
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      setStream(mediaStream);
      if (videoRef.current) videoRef.current.srcObject = mediaStream;
      setShowCamera(true);
    } catch (err) {
      console.error("Camera access denied", err);
      alert("Please allow camera access.");
    }
  };

  const capturePhoto = () => {
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    setImagePreview(canvas.toDataURL("image/jpeg"));
    stopCamera();
  };

  const stopCamera = () => {
    stream?.getTracks().forEach((track) => track.stop());
    setShowCamera(false);
  };

  // ── Messages & send handlers ────────────────────────────────────────────────
  const showSuggestions = inputValue.trim().length > 0;

  const handleSuggestionClick = (type) => {
    const text =
      type === "statement"
        ? `show statement of ${inputValue}`
        : `show outstanding of ${inputValue}`;
    setInputValue(text);
  };

  const getWelcomeMessage = (m) =>
    m === "support"
      ? "Hi! I'm Cubina Support. Describe your issue and I'll help resolve it or raise a ticket."
      : "Hello! I'm Cubina. How can I help you navigate your ERP today?";

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hello! I'm Cubina. How can I help you navigate your ERP today?",
      time: "Just now",
    },
  ]);
  const [loading, setLoading] = useState(false);

  // Switch mode: reset messages + support history
  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setSupportChatHistory([]);
    setMessages([
      { role: "assistant", text: getWelcomeMessage(newMode), time: "Just now" },
    ]);
    setInputValue("");
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── General chat send ───────────────────────────────────────────────────────
  const handleSendGeneral = async (textOverride = "") => {
    const query = textOverride || inputValue;
    if (!query.trim()) return;

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: query,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
    setInputValue("");
    setLoading(true);

    try {
      const payload = {
        Question: query,
        CmpCode: COMPANY_CODE,
        DeptNo: DEPARTMENT,
        History: [],
      };
      const response = await axios.post(`${BASE_URL}/api/chatbot/ask`, payload);
      const resData = response.data;
      const intent = resData.intent?.intent || resData.intent;

      let assistantMessage = {
        role: "assistant",
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        intent,
        tableData: resData.data || [],
      };

      if (intent === "STATEMENT_OF_ACCOUNT" && resData.data?.length > 0) {
        const lastRow = resData.data[resData.data.length - 1];
        assistantMessage.text = `Statement of ${resData.customerName || "Account"}`;
        assistantMessage.accountInfo = {
          name: resData.customerName,
          code: resData.account,
          balance: lastRow.BALANCE || 0,
        };
      } else if (
        intent === "OUTSTANDING_OF_ACCOUNT" &&
        resData.data?.length > 0
      ) {
        const outstandingVal = resData.data[0]["Outstanding Balance"] || 0;
        assistantMessage.text = `Outstanding Balance for ${resData.customerName || "Account"}`;
        assistantMessage.accountInfo = {
          name: resData.customerName,
          code: resData.account,
          balance: outstandingVal,
          isOutstanding: true,
        };
        assistantMessage.tableData = [];
      } else if (intent === "unknown") {
        assistantMessage.text =
          "I'm sorry, I couldn't find that information. Could you try rephrasing?";
      } else {
        assistantMessage.text = `Here is the ${intent?.replace(/_/g, " ") || "requested data"}:`;
      }

      setMessages((prev) => [...prev, assistantMessage]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Connection error.", time: "Now" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ── Support escalation ──────────────────────────────────────────────────────
  const handleEscalate = () => {
    handleSendSupport("need escalation");
  };

  // ── Support chat send ───────────────────────────────────────────────────────
  const handleSendSupport = async (textOverride = "") => {
    const query = textOverride || inputValue;
    if (!query.trim()) return;

    const userMsg = {
      role: "user",
      text: query,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("ClientCompanyCode", COMPANY_CODE);
      formData.append("UserId", USER_ID);
      formData.append("UserMessage", query);
      formData.append(
        "ChatHistory",
        supportChatHistory.length > 0 ? JSON.stringify(supportChatHistory) : "",
      );
      formData.append("Screenshot", "");

      const response = await axios.post(
        `${BASE_URL}/api/AiSupport/chat`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const res = response.data;

      // Update rolling chat history for next turn
      if (res.chatHistory) {
        setSupportChatHistory(res.chatHistory);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          isSupportResponse: true,
          chatResponseUser: res.chatResponseUser,
          chatResponseTechnical: res.chatResponseTechnical,
          resolvedByAI: res.resolvedByAI,
          escalate: res.escalate,
          ticketNo: res.ticketNo,
          confidenceScore: res.confidenceScore,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Support service unavailable. Please try again.",
          time: "Now",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ── Unified send dispatcher ─────────────────────────────────────────────────
  const handleSend = (textOverride = "") => {
    if (imagePreview) return handleImageSubmit();
    if (mode === "support") return handleSendSupport(textOverride);
    return handleSendGeneral(textOverride);
  };

  // ── Table component ─────────────────────────────────────────────────────────
  const ChatTable = ({ data }) => {
    if (!data || data.length === 0) return null;
    const headers = Object.keys(data[0]);
    return (
      <div className="mt-3 border rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto max-h-64">
          <table className="w-full text-[11px] text-left border-collapse">
            <thead className="sticky top-0 bg-slate-100 shadow-sm">
              <tr>
                {headers.map((key) => (
                  <th
                    key={key}
                    className="p-2 font-bold text-slate-600 uppercase border-b"
                  >
                    {key.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((row, i) => (
                <tr key={i} className="hover:bg-blue-50/50">
                  {headers.map((key) => (
                    <td
                      key={key}
                      className="p-2 text-slate-600 whitespace-nowrap"
                    >
                      {typeof row[key] === "number"
                        ? row[key].toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })
                        : row[key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getPlaceholderText = () => {
    if (isRecording)
      return imagePreview ? "Listening for category..." : "Listening...";
    if (isProcessing) return "Processing audio...";
    if (mode === "support") return "Describe your issue...";
    return "Type your request...";
  };

  // ── Header gradient by mode ─────────────────────────────────────────────────
  const headerGradient =
    mode === "support"
      ? "bg-gradient-to-r from-indigo-600 to-violet-700"
      : "bg-gradient-to-r from-blue-600 to-indigo-700";

  const sendButtonColor = imagePreview
    ? "bg-indigo-600 hover:bg-indigo-700"
    : mode === "support"
      ? "bg-violet-600 hover:bg-violet-700"
      : "bg-blue-600 hover:bg-blue-700";

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      ref={assistantRef}
      className="mb-2 w-[400px] h-[580px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
    >
      {/* ── Header ── */}
      <div className={`p-4 ${headerGradient} text-white shadow-md`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 p-1.5 rounded-lg">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold leading-tight">Cubina AI</h3>
              <p className="text-[10px] text-blue-100 capitalize">
                {mode} mode
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModeToggle mode={mode} onChange={handleModeChange} />
          </div>
        </div>
      </div>

      {/* ── Support mode banner ── */}
      {mode === "support" && (
        <div className="flex items-center gap-2 px-4 py-2 bg-violet-50 border-b border-violet-100">
          <HeadphonesIcon className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
          <p className="text-[10px] text-violet-600 font-medium">
            Support chat — issues are tracked and can be escalated to support
            team.
          </p>
        </div>
      )}

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
          >
            <div
              className={`p-3 rounded-2xl text-sm max-w-[90%] shadow-sm ${
                msg.role === "user"
                  ? mode === "support"
                    ? "bg-violet-600 text-white rounded-tr-none"
                    : "bg-blue-600 text-white rounded-tr-none"
                  : "bg-white border border-slate-200 text-slate-700 rounded-tl-none"
              }`}
            >
              {msg.image && (
                <img
                  src={msg.image}
                  className="w-full h-32 object-cover rounded-lg mb-2 border"
                  alt="upload"
                />
              )}

              {/* Support response */}
              {msg.isSupportResponse ? (
                <SupportMessage msg={msg} onEscalate={handleEscalate} />
              ) : msg.isRichResponse ? (
                /* Rich (image identification) response */
                <div className="space-y-1">
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-bold text-blue-600 leading-tight">
                      {msg.objectName}
                    </h4>
                    <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-bold">
                      {Math.round(msg.confidence * 100)}% Match
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                    {msg.category}
                  </p>
                  {(msg.OemPartNumber ||
                    (msg.alternativePartNumbers &&
                      msg.alternativePartNumbers.length > 0)) && (
                    <div className="grid grid-cols-1 gap-1.5 py-2 border-y border-slate-100 my-2 animate-in fade-in duration-300">
                      {msg.OemPartNumber && (
                        <div className="flex items-center justify-between bg-slate-50 p-1.5 rounded-lg border border-slate-100 group/item">
                          <div className="flex flex-col">
                            <span className="text-[9px] font-bold text-slate-400 uppercase leading-none mb-0.5">
                              OEM Part #
                            </span>
                            <span className="text-[11px] font-mono font-bold text-slate-700">
                              {msg.OemPartNumber}
                            </span>
                          </div>
                          <div>
                            <button
                              onClick={() =>
                                navigator.clipboard.writeText(msg.OemPartNumber)
                              }
                              className="p-1 hover:bg-white rounded border border-transparent hover:border-slate-200 text-slate-400 hover:text-blue-600 transition-all"
                              title="Copy Number"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                            <button
                              title="View Stock List"
                              onClick={() =>
                                navigate("/stock-list", {
                                  state: {
                                    searchQuery: msg.OemPartNumber,
                                    filterBy: "OEM",
                                  },
                                })
                              }
                              className="p-1 hover:bg-white rounded border border-transparent hover:border-slate-200 text-slate-400 hover:text-blue-600 transition-all"
                            >
                              <Link className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      {msg.alternativePartNumbers &&
                        msg.alternativePartNumbers.length > 0 && (
                          <div className="flex flex-col bg-blue-50/50 p-1.5 rounded-lg border border-blue-100">
                            <span className="text-[9px] font-bold text-blue-400 uppercase mb-1">
                              Aftermarket / Alternatives
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(msg.alternativePartNumbers) ? (
                                msg.alternativePartNumbers.map((num, i) => (
                                  <span
                                    key={i}
                                    className="text-[10px] bg-white px-1.5 py-0.5 rounded border border-blue-100 text-blue-700 font-medium shadow-sm"
                                  >
                                    {num}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[11px] font-mono text-blue-700">
                                  {msg.alternativePartNumbers}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                  <p className="text-xs leading-relaxed text-slate-600 mt-1">
                    {msg.description}
                  </p>
                  <PlatformLinks links={msg.links} />
                </div>
              ) : (
                /* Standard text response */
                <div className="space-y-2">
                  <p className="whitespace-pre-wrap">{msg.text}</p>
                  {msg.accountInfo && (
                    <div className="py-1 border-t border-slate-100 flex justify-between items-center">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase tracking-tight">
                          Account
                        </span>
                        <p className="text-xs text-slate-600 leading-none">
                          {msg.accountInfo.code}
                        </p>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-[10px] text-slate-400 uppercase tracking-tight">
                          {msg.accountInfo.isOutstanding
                            ? "Total Outstanding"
                            : "Closing Balance"}
                        </span>
                        <p
                          className={`text-xs font-semibold ${msg.accountInfo.balance < 0 ? "text-red-500" : "text-slate-700"}`}
                        >
                          {msg.accountInfo.balance.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                  {msg.tableData && msg.tableData.length > 0 && (
                    <div className="mt-1">
                      <ChatTable data={msg.tableData} />
                    </div>
                  )}
                </div>
              )}
            </div>
            <span className="text-[10px] text-slate-400 mt-1 mx-1">
              {msg.time}
            </span>
          </div>
        ))}

        {loading && (
          <div className="flex items-start animate-pulse">
            <div className="bg-white border border-slate-200 p-3 rounded-2xl rounded-tl-none text-xs text-slate-400">
              {mode === "support"
                ? "Support AI is analyzing..."
                : "Cubina is thinking..."}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Input area ── */}
      <div className="p-4 bg-white border-t border-slate-100">
        <input
          type="file"
          ref={fileInputRef}
          hidden
          accept="image/*"
          onChange={handleImageSelect}
        />

        {/* Image preview */}
        {imagePreview && (
          <div className="flex items-center gap-3 mb-3 p-2 bg-indigo-50 border border-indigo-100 rounded-xl animate-in fade-in slide-in-from-bottom-2">
            <div className="relative h-12 w-12 flex-shrink-0">
              <img
                src={imagePreview}
                className="h-full w-full object-cover rounded-lg border border-white shadow-sm"
                alt="preview"
              />
              <button
                onClick={() => {
                  setSelectedImage(null);
                  setImagePreview(null);
                  setTargetCategory("");
                }}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shadow-sm"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-tight">
                Image Identification Mode
              </p>
              <p className="text-[11px] text-slate-500">
                Specify industry in the box below
              </p>
            </div>
          </div>
        )}

        {/* Quick suggestion pills — general mode only */}
        {mode === "general" &&
          !showSuggestions &&
          !isRecording &&
          !isProcessing &&
          !imagePreview && (
            <div className="flex gap-2 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-200">
              <button
                onClick={() => handleSuggestionClick("statement")}
                className="px-3 py-1.5 bg-blue-50 border border-blue-100 text-blue-600 rounded-full text-[10px] font-bold uppercase tracking-wide hover:bg-blue-600 hover:text-white transition-all shadow-sm flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> View Statement
              </button>
              <button
                onClick={() => handleSuggestionClick("outstanding")}
                className="px-3 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-full text-[10px] font-bold uppercase tracking-wide hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Show Outstanding
              </button>
            </div>
          )}

        {/* Support quick-action pills */}
        {mode === "support" && !inputValue && !isRecording && !isProcessing && (
          <div className="flex flex-wrap gap-2 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-200">
            {["Login issue", "Report error", "Data mismatch"].map((s) => (
              <button
                key={s}
                onClick={() => setInputValue(s)}
                className="px-3 py-1.5 bg-violet-50 border border-violet-100 text-violet-600 rounded-full text-[10px] font-bold uppercase tracking-wide hover:bg-violet-600 hover:text-white transition-all shadow-sm"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Main action row */}
        <div className="relative flex items-center gap-2">
          {/* Image upload — general mode only */}
          {mode === "general" && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isRecording || isProcessing}
                className={`p-3 rounded-2xl transition-all ${imagePreview ? "bg-indigo-600 text-white shadow-md shadow-indigo-100" : "bg-slate-100 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600"} disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Upload Image"
              >
                <ImageIcon className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Text input */}
          <div className="relative flex-1">
            <input
              id="cubina-input"
              autoFocus
              value={imagePreview ? targetCategory : inputValue}
              onChange={(e) =>
                imagePreview
                  ? setTargetCategory(e.target.value)
                  : setInputValue(e.target.value)
              }
              disabled={isProcessing}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isRecording && !isProcessing)
                  handleSend();
              }}
              placeholder={
                imagePreview
                  ? "Enter target industry (e.g. Auto)..."
                  : getPlaceholderText()
              }
              className={`w-full pl-4 pr-12 py-3 bg-slate-100 border-none rounded-2xl text-sm focus:ring-2 ${
                mode === "support"
                  ? "focus:ring-violet-500"
                  : "focus:ring-blue-500"
              } transition-all outline-none text-slate-700 ${
                isRecording || isProcessing
                  ? "text-blue-500 font-medium animate-pulse placeholder:text-blue-300"
                  : ""
              }`}
            />

            {!isRecording && !isProcessing && (
              <button
                onClick={() => handleSend()}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl text-white transition-all transform active:scale-95 ${sendButtonColor} shadow-sm`}
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Mic */}
          <button
            onClick={toggleRecording}
            disabled={isProcessing}
            className={`p-3 rounded-2xl transition-all duration-300 ${
              isRecording
                ? "bg-red-500 text-white animate-pulse shadow-lg shadow-red-200"
                : isProcessing
                  ? "bg-slate-300 text-slate-400 cursor-not-allowed"
                  : "bg-slate-100 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
            }`}
          >
            {isRecording ? (
              <Square className="w-5 h-5 fill-current" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Camera overlay */}
        {showCamera && (
          <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-4">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="rounded-xl w-full max-w-md"
            />
            <div className="flex gap-4 mt-6">
              <button
                onClick={stopCamera}
                className="px-6 py-2 bg-white/20 text-white rounded-full"
              >
                Cancel
              </button>
              <button
                onClick={capturePhoto}
                className="px-8 py-4 bg-emerald-500 text-white rounded-full font-bold shadow-lg"
              >
                CAPTURE
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatAssistantModal;
